'use client';
import React, { useState } from 'react';
import {
  FileText, Search, Filter, Download, ChevronDown,
  User, Settings, DollarSign, Shield, AlertTriangle,
  Lock, UserX, Edit, Eye, RefreshCw
} from 'lucide-react';

interface AuditLog {
  id: string;
  actor: string;
  actorRole: string;
  action: string;
  target: string;
  targetType: string;
  prevValue: string;
  newValue: string;
  ip: string;
  timestamp: string;
  category: 'user' | 'finance' | 'system' | 'security' | 'content';
  reason: string;
}

const logs: AuditLog[] = [
  { id: 'LOG-99821', actor: 'Admin Sarah', actorRole: 'Super Admin', action: 'USER_BANNED', target: 'Liu Wei (USR-09821)', targetType: 'User', prevValue: 'active', newValue: 'banned', ip: '10.0.1.45', timestamp: 'Aug 8, 2026 — 20:14:32', category: 'user', reason: 'Confirmed bot usage in quiz competitions' },
  { id: 'LOG-99820', actor: 'Finance Manager', actorRole: 'Finance Manager', action: 'WITHDRAWAL_APPROVED', target: 'TXN-98420 ($250)', targetType: 'Transaction', prevValue: 'pending', newValue: 'approved', ip: '10.0.1.46', timestamp: 'Aug 8, 2026 — 19:58:11', category: 'finance', reason: 'KYC verified, no fraud flags' },
  { id: 'LOG-99819', actor: 'Admin John', actorRole: 'Super Admin', action: 'COMMISSION_UPDATED', target: 'Global Commission Rate', targetType: 'System Setting', prevValue: '10%', newValue: '12%', ip: '10.0.1.45', timestamp: 'Aug 8, 2026 — 18:30:00', category: 'system', reason: 'Q3 revenue adjustment' },
  { id: 'LOG-99818', actor: 'Moderator Kim', actorRole: 'Moderator', action: 'WALLET_FROZEN', target: 'James Wilson (USR-15892)', targetType: 'User Wallet', prevValue: 'active', newValue: 'frozen', ip: '10.0.1.47', timestamp: 'Aug 8, 2026 — 17:22:45', category: 'security', reason: 'Suspicious withdrawal pattern under investigation' },
  { id: 'LOG-99817', actor: 'Admin Sarah', actorRole: 'Super Admin', action: 'ACTIVITY_PUBLISHED', target: 'Daily Trivia Championship (ACT-001)', targetType: 'Activity', prevValue: 'draft', newValue: 'published', ip: '10.0.1.45', timestamp: 'Aug 8, 2026 — 16:05:20', category: 'content', reason: 'Scheduled publication' },
  { id: 'LOG-99816', actor: 'Admin John', actorRole: 'Super Admin', action: 'USER_SUSPENDED', target: 'Carlos Mendez (USR-22891)', targetType: 'User', prevValue: 'active', newValue: 'suspended', ip: '10.0.1.45', timestamp: 'Aug 8, 2026 — 15:40:10', category: 'user', reason: 'Referral farming investigation' },
  { id: 'LOG-99815', actor: 'System', actorRole: 'Automated', action: 'FRAUD_ALERT_CREATED', target: 'FRD-004 (Bot Detection)', targetType: 'Fraud Alert', prevValue: 'none', newValue: 'open', ip: 'System', timestamp: 'Aug 8, 2026 — 14:55:00', category: 'security', reason: 'Automated fraud detection triggered' },
  { id: 'LOG-99814', actor: 'Finance Manager', actorRole: 'Finance Manager', action: 'WITHDRAWAL_HELD', target: 'TXN-98417 ($4,500)', targetType: 'Transaction', prevValue: 'pending', newValue: 'review', ip: '10.0.1.46', timestamp: 'Aug 8, 2026 — 14:10:33', category: 'finance', reason: 'Amount exceeds threshold — manual review required' },
  { id: 'LOG-99813', actor: 'Admin Sarah', actorRole: 'Super Admin', action: 'REGION_ELIGIBILITY_UPDATED', target: 'Speed Coding Hackathon (ACT-002)', targetType: 'Activity', prevValue: 'Global', newValue: 'USA, EU, India', ip: '10.0.1.45', timestamp: 'Aug 8, 2026 — 13:20:00', category: 'content', reason: 'Compliance requirement for paid competition' },
  { id: 'LOG-99812', actor: 'Admin John', actorRole: 'Super Admin', action: 'ADMIN_LOGIN', target: 'Admin Panel', targetType: 'System', prevValue: '', newValue: 'logged_in', ip: '10.0.1.45', timestamp: 'Aug 8, 2026 — 09:00:00', category: 'security', reason: '' },
];

const categoryConfig = {
  user: { color: '#7C3AED', bg: 'rgba(124,58,237,0.12)', icon: User, label: 'User' },
  finance: { color: '#10B981', bg: 'rgba(16,185,129,0.12)', icon: DollarSign, label: 'Finance' },
  system: { color: '#06B6D4', bg: 'rgba(6,182,212,0.12)', icon: Settings, label: 'System' },
  security: { color: '#F43F5E', bg: 'rgba(244,63,94,0.12)', icon: Shield, label: 'Security' },
  content: { color: '#F59E0B', bg: 'rgba(245,158,11,0.12)', icon: FileText, label: 'Content' },
};

const actionIcons: Record<string, React.ElementType> = {
  USER_BANNED: UserX,
  USER_SUSPENDED: Lock,
  WALLET_FROZEN: Lock,
  WITHDRAWAL_APPROVED: DollarSign,
  WITHDRAWAL_HELD: AlertTriangle,
  COMMISSION_UPDATED: Settings,
  ACTIVITY_PUBLISHED: Edit,
  FRAUD_ALERT_CREATED: AlertTriangle,
  REGION_ELIGIBILITY_UPDATED: Edit,
  ADMIN_LOGIN: Shield,
};

export default function AdminAuditLogsContent() {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [expanded, setExpanded] = useState<string | null>(null);

  const filtered = logs.filter(l => {
    const ms = !search || l.action.toLowerCase().includes(search.toLowerCase()) ||
      l.actor.toLowerCase().includes(search.toLowerCase()) || l.target.toLowerCase().includes(search.toLowerCase());
    const mc = categoryFilter === 'all' || l.category === categoryFilter;
    return ms && mc;
  });

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Audit Logs</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--muted-foreground)' }}>Complete immutable record of all admin and system actions</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-ghost text-xs py-2 px-3 flex items-center gap-1.5"><Download size={14} /> Export</button>
          <button className="btn-ghost text-xs py-2 px-3 flex items-center gap-1.5"><RefreshCw size={14} /> Refresh</button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {Object.entries(categoryConfig).map(([key, cfg]) => (
          <div key={key} className="rounded-xl p-3 cursor-pointer transition-all"
            style={{
              background: 'var(--card)',
              border: `1px solid ${categoryFilter === key ? cfg.color + '60' : 'var(--border)'}`,
            }}
            onClick={() => setCategoryFilter(categoryFilter === key ? 'all' : key)}>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: cfg.bg }}>
                <cfg.icon size={13} style={{ color: cfg.color }} />
              </div>
              <span className="text-xs font-semibold" style={{ color: cfg.color }}>{cfg.label}</span>
            </div>
            <p className="text-lg font-bold text-foreground">{logs.filter(l => l.category === key).length}</p>
          </div>
        ))}
      </div>

      {/* Search & Filter */}
      <div className="flex flex-wrap items-center gap-3 p-4 rounded-xl" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
        <div className="relative flex-1 min-w-48">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
          <input type="text" placeholder="Search by action, actor, target..." value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm rounded-lg outline-none"
            style={{ background: 'var(--muted)', border: '1px solid var(--border)', color: 'var(--foreground)' }} />
        </div>
        <div className="relative">
          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
            className="appearance-none pl-3 pr-8 py-2 text-sm rounded-lg outline-none cursor-pointer"
            style={{ background: 'var(--muted)', border: '1px solid var(--border)', color: 'var(--foreground)' }}>
            <option value="all">All Categories</option>
            <option value="user">User</option>
            <option value="finance">Finance</option>
            <option value="system">System</option>
            <option value="security">Security</option>
            <option value="content">Content</option>
          </select>
          <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--muted-foreground)' }} />
        </div>
        <span className="text-xs ml-auto" style={{ color: 'var(--muted-foreground)' }}>{filtered.length} entries</span>
      </div>

      {/* Log Entries */}
      <div className="rounded-xl overflow-hidden" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--muted)' }}>
                {['Log ID', 'Actor', 'Action', 'Target', 'Category', 'Timestamp', ''].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider"
                    style={{ color: 'var(--muted-foreground)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((log) => {
                const cc = categoryConfig[log.category];
                const ActionIcon = actionIcons[log.action] || FileText;
                const isExpanded = expanded === log.id;
                return (
                  <React.Fragment key={log.id}>
                    <tr className="border-b hover:bg-muted/50 transition-colors cursor-pointer"
                      style={{ borderColor: 'var(--border)' }}
                      onClick={() => setExpanded(isExpanded ? null : log.id)}>
                      <td className="px-4 py-3 text-xs font-mono" style={{ color: '#A78BFA' }}>{log.id}</td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-xs font-medium text-foreground">{log.actor}</p>
                          <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{log.actorRole}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <ActionIcon size={13} style={{ color: cc.color }} />
                          <span className="text-xs font-mono font-semibold" style={{ color: cc.color }}>{log.action}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-foreground max-w-48 truncate">{log.target}</td>
                      <td className="px-4 py-3">
                        <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                          style={{ background: cc.bg, color: cc.color }}>{cc.label}</span>
                      </td>
                      <td className="px-4 py-3 text-xs" style={{ color: 'var(--muted-foreground)' }}>{log.timestamp}</td>
                      <td className="px-4 py-3">
                        <Eye size={13} style={{ color: 'var(--muted-foreground)' }} />
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr style={{ background: 'rgba(124,58,237,0.04)', borderBottom: '1px solid var(--border)' }}>
                        <td colSpan={7} className="px-4 py-4">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                            <div>
                              <p className="font-semibold mb-1" style={{ color: 'var(--muted-foreground)' }}>Previous Value</p>
                              <p className="font-mono text-foreground">{log.prevValue || '—'}</p>
                            </div>
                            <div>
                              <p className="font-semibold mb-1" style={{ color: 'var(--muted-foreground)' }}>New Value</p>
                              <p className="font-mono text-foreground">{log.newValue || '—'}</p>
                            </div>
                            <div>
                              <p className="font-semibold mb-1" style={{ color: 'var(--muted-foreground)' }}>IP Address</p>
                              <p className="font-mono text-foreground">{log.ip}</p>
                            </div>
                            <div>
                              <p className="font-semibold mb-1" style={{ color: 'var(--muted-foreground)' }}>Reason</p>
                              <p className="text-foreground">{log.reason || 'No reason provided'}</p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t" style={{ borderColor: 'var(--border)' }}>
          <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Showing {filtered.length} of 99,821 log entries</span>
          <div className="flex items-center gap-1">
            {[1, 2, 3, '...', 998].map((p, i) => (
              <button key={`page-${i}`} className="w-7 h-7 rounded-md text-xs font-medium transition-colors"
                style={{ background: p === 1 ? '#7C3AED' : 'var(--muted)', color: p === 1 ? '#fff' : 'var(--muted-foreground)' }}>
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
